/* eslint-disable no-use-before-define */
import React, { useReducer, useEffect, useContext } from 'react';

// Components and others
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
    findIndex, orderBy, concat,
} from 'lodash';
import update from 'immutability-helper';
import ModalUtils from 'utils/ModalUtils';
import LotFilter from 'components/widgets/LotFilter';
import UserContext from 'components/context/UserContext';
import InputSearch from 'components/widgets/InputSearch';
import MessageCenterCustomerContext from 'components/context/MessageCenterCustomerContext';

// Material Ui
// import FilterListIcon from '@material-ui/icons/FilterList';
import {
    List,
    makeStyles,
    Typography,
    Switch,
} from '@material-ui/core';

// HTTP
import { FetchPolicy, MEDIA_QUERY } from 'utils/enum/Core';
import { useQuery, useSubscription } from '@apollo/client';
import InfiniteScroll from 'components/widgets/InfiniteScroll';
import SubscriptionActionType from 'utils/enum/SubscriptionActionType';
import MessageItem from 'components/modules/messageCenter/list/MessageItem';
import MessageCenterQuery from 'services/graphQL/query/crm/MessageCenterQuery';
import MessageCenterSubscription from 'services/graphQL/subscription/crm/MessageCenterSubscription';

const useStyles = makeStyles((theme) => ({
    containerSidebar: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.primary,
    },
    inputSearchContainer: {
        display: 'flex',
        padding: theme.spacing(2, 3),
        flexDirection: 'column',
        '& input': {
            background: 'transparent',
            paddingRight: theme.spacing(7),
        },
        '& input:focus': {
            background: 'transparent',
        },
        '& > div:nth-child(1)': {
            marginBottom: '15px',
        },
    },
    box: {
        width: 320,
        minWidth: 320,
        padding: theme.spacing(3),
        overflow: 'auto',
        paddingTop: 0,
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            width: '100%',
            minWidth: 'unset',
            padding: theme.spacing(2),
        },
    },
    list: {
        padding: 0,
    },
    filterListIcon: {
        marginRight: theme.spacing(0.5),
    },
    label: {
        fontSize: '15px',
        fontWeight: 500,
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1),
        color: theme.palette.text.boulderGray,
    },
    dropDownLot: {
        width: '230px',
        background: theme.palette.background.white,
        borderRadius: 5,
        border: `1px solid ${theme.palette.border.primary}`,
        marginLeft: '15px',
    },
}));

const initState = {
    records: [],
    start: 0,
    totalCount: 0,
    search: '',
    lots: null,
    readyToPull: false,
    showUnansweredSMS: true,
};

const ACTION_TYPES = {
    SET_START: 'setStart',
    ON_SEARCH: 'onSearch',
    LOAD_MORE: 'loadMore',
    ADD_RECORD: 'addRecord',
    RECORD_UPDATED: 'recordUpdated',
    ON_CLEAR_SEARCH: 'onClearSearch',
    ON_CHANGE_VALUE: 'onChangeValue',
};

const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.LOAD_MORE:
        const record = action.payload;
        const newRecords = concat(state.records, record.data);
        return update(state, {
            records: { $set: newRecords },
            totalCount: { $set: record.totalCount },
        });
    case ACTION_TYPES.SET_START:
        return update(state, {
            start: { $set: action.payload },
        });
    case ACTION_TYPES.ADD_RECORD:
        return update(state, {
            records: { $unshift: [action.payload] },
            totalCount: { $set: state.totalCount + 1 },
        });
    case ACTION_TYPES.RECORD_UPDATED:
        const recordUpdated = action.payload;
        const index = findIndex(state.records, { smsChatId: recordUpdated.smsChatId });

        if (index >= 0) {
            const result = orderBy(update(state.records, {
                [index]: {
                    lastUpdated: { $set: recordUpdated.lastUpdated },
                    sms: {
                        status: { $set: recordUpdated.sms.status },
                        direction: { $set: recordUpdated.sms.direction },
                    },
                },
            }), ['lastUpdated'], ['desc']);

            return { ...state, records: result };
        }

        return state;
    case ACTION_TYPES.ON_SEARCH:
        return update(state, {
            search: { $set: action.value },
            totalCount: { $set: 0 },
            records: { $set: [] },
        });
    case ACTION_TYPES.ON_CHANGE_VALUE:
        return update(state, {
            [action.field]: { $set: action.value },
            totalCount: { $set: 0 },
            readyToPull: { $set: true },
            start: { $set: 0 },
            records: { $set: [] },
        });
    case ACTION_TYPES.ON_CLEAR_SEARCH:
        return update(state, {
            search: { $set: '' },
        });
    default:
        return state;
    }
};
const MessageCenterList = ({ conversationId }) => {
    const classes = useStyles();
    const { availableLots, defaultLotId, defaultLot } = useContext(UserContext);
    const { setCustomer } = useContext(MessageCenterCustomerContext);
    const [state, dispatch] = useReducer(reducer, initState);

    const availableLot = availableLots.map((item) => ({ value: item.id, label: item.label }));
    const input = {
        paging: {
            start: state.start,
            limit: 50,
        },
        lots: (state.lots ?? []).map((item) => item.value),
        search: state.search,
        showUnansweredSMS: state.showUnansweredSMS,
    };

    const {
        loading, data, error,
    } = useQuery(MessageCenterQuery.GET_SMS_CHAT, {
        variables: input,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
        skip: !state.readyToPull,
    });

    const { data: subscriptionData } = useSubscription(MessageCenterSubscription.SMS_CHAT_UPDATED);
    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
        }

        if (!loading && data?.getSMSChat) {
            dispatch({
                type: ACTION_TYPES.LOAD_MORE,
                payload: data.getSMSChat,
            });
        }
    }, [loading, data, error]);

    useEffect(() => {
        if (subscriptionData) {
            const { smsChatUpdated } = subscriptionData;
            if (smsChatUpdated?.type?.toUpperCase() === SubscriptionActionType.CREATED) {
                dispatch({
                    type: ACTION_TYPES.ADD_RECORD,
                    payload: smsChatUpdated.smsChat,
                });
            }

            if (smsChatUpdated?.type?.toUpperCase() === SubscriptionActionType.UPDATED) {
                dispatch({
                    type: ACTION_TYPES.RECORD_UPDATED,
                    payload: smsChatUpdated.smsChat,
                });
            }
        }
    }, [subscriptionData]);

    useEffect(() => {
        if (conversationId != null) {
            const selectedRecord = state.records.find((item) => item.smsChatId === conversationId);
            if (selectedRecord) {
                setCustomer({
                    ...selectedRecord?.customer,
                    opportunity: {
                        leadCode: selectedRecord.sms?.opportunity?.leadCode,
                        crmId: selectedRecord.sms?.opportunity?.crmId,
                    },
                });
            }
        }
    }, [conversationId, state.records, setCustomer]);

    useEffect(() => {
        if (availableLot.length > 0) {
            const { value, label } = availableLot.find((al) => al.label === defaultLot) ?? {};
            if (value && label && !conversationId) {
                onChangeValue([{ value, label }], 'lots');
            } else {
                onChangeValue([], 'lots');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultLotId]);

    const loadMore = () => {
        const start = state.records.length;
        dispatch({
            type: ACTION_TYPES.SET_START,
            payload: start,
        });
    };

    const onSearch = (value) => {
        if (state.search !== value) {
            dispatch({ type: ACTION_TYPES.ON_SEARCH, value });
        }
    };
    const onClearSearch = () => {
        dispatch({ type: ACTION_TYPES.ON_CLEAR_SEARCH });
    };

    const onChangeValue = (value, field) => {
        dispatch({
            type: ACTION_TYPES.ON_CHANGE_VALUE,
            field,
            value,
        });
    };

    return (
        <div
            className={classes.containerSidebar}
        >
            <div className={classes.inputSearchContainer}>
                <div>
                    <div className={clsx('d-flex-align-baseline-space-between', classes.lotContainer)}>
                        <Typography
                            variant="h5"
                            className={classes.label}
                        >
                            Lot:
                        </Typography>
                        {state.readyToPull && (
                            <LotFilter
                                onSearch={
                                    (values) => onChangeValue(
                                        availableLot.filter((l) => values.includes(l.label)).map(({ value, label }) => ({ value, label })), 'lots',
                                    )
                                }
                                initialLots={state.lots.map((l) => l.label)}
                                className={classes.dropDownLot}
                            />
                        )}
                    </div>
                    <div className="d-flex-align-baseline-space-between">
                        <Typography
                            variant="h5"
                            className={classes.label}
                        >
                            Unanswered:
                        </Typography>
                        <Switch
                            checked={state.showUnansweredSMS}
                            onChange={(event) => onChangeValue(event.target.checked, 'showUnansweredSMS')}
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                    </div>
                </div>
                <InputSearch
                    size="sm"
                    forceSearch
                    initialSearch={state.search}
                    customClasses={classes.search}
                    onSearch={onSearch}
                    executeWhenClearButton={onClearSearch}
                />
            </div>
            <InfiniteScroll
                load={loading}
                loadMore={loadMore}
                className={classes.box}
                loadAtScrollPercent={70}
                totalRecord={state.totalCount}
                lengthRecord={state.records.length}
            >
                <List className={classes.list}>
                    {state.records.map((item, index) => <MessageItem record={item} key={index} conversationId={conversationId} />)}
                </List>
            </InfiniteScroll>
        </div>
    );
};

MessageCenterList.propTypes = {
    conversationId: PropTypes.string,
};

MessageCenterList.defaultProps = {
    conversationId: '',
};

export default MessageCenterList;
